import { BaseText, Button, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import { LoyaltyLogo } from 'components/services/LoyaltyLogo';
import AccountContext from 'context/AccountContext';
import { format } from 'date-fns';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useBenefitStore } from 'stores/benefitStore';
import {
  CenterVerticalRow,
  ColumnWithMargins,
  ItemWrapper,
  RowSpaceBetween,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { BenefitAdmissionDetail } from 'types/generatedSchemaTypes';
import { collectIdentifiers, getVerifiedEmail } from 'utils/benefitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { benefitLabelMap } from './BenefitCard';
import { useUiStore } from 'stores/uiStore';

const getBenefitLabel = (benefitTargetType: string): string =>
  benefitLabelMap[benefitTargetType] ?? 'account.benefit.parkingBenefit';

const formatDate = (value: string | Date): string => {
  try {
    const date = value instanceof Date ? value : new Date(value);
    return format(date, 'd MMM, yyyy');
  } catch {
    return String(value);
  }
};

const getLatestVerifiedAt = (
  admissionDetails: Array<BenefitAdmissionDetail | null> | null | undefined
): string | undefined => {
  let latest: string | undefined;
  admissionDetails?.forEach((detail) => {
    detail?.attributes?.forEach((attr) => {
      if (attr?.verifiedAt && (!latest || attr.verifiedAt > latest)) {
        latest = attr.verifiedAt;
      }
    });
  });
  return latest;
};

const BenefitDetailModal: React.FC = () => {
  const { t } = useTranslation();
  const { benefit } = useBenefitStore((state) => state);
  const { customer } = useContext(AccountContext);

  const admissionDetails = benefit?.customerMemberMetadata?.admissionDetails;

  const identifiers = useMemo(
    () => collectIdentifiers(admissionDetails, customer.contactEmail),
    [admissionDetails, customer.contactEmail]
  );

  const verifiedAt = useMemo(
    () => getLatestVerifiedAt(admissionDetails),
    [admissionDetails]
  );

  const showDisconnect = !!getVerifiedEmail(
    admissionDetails,
    customer.contactEmail
  );

  if (!benefit) {
    return null;
  }

  const label = t(getBenefitLabel(benefit.benefitTargetType));
  const validFrom = benefit.customerMemberMetadata?.validity?.validFrom;

  return (
    <View>
      <StyledWrapper mb={24}>
        <CenterVerticalRow>
          {benefit.logo ? (
            <LoyaltyLogo uri={benefit.logo} width="48px" height="48px" />
          ) : (
            <View style={styles.iconCircle}>
              <Icon size="m" name="Business-Outlined" />
            </View>
          )}
          <ColumnWithMargins ml={16} style={styles.flex1}>
            <Meta color={theme.colors.gray300}>{label}</Meta>
            <BaseText style={styles.benefitName}>{benefit.name}</BaseText>
          </ColumnWithMargins>
        </CenterVerticalRow>
      </StyledWrapper>

      {identifiers.length > 0 && (
        <ItemWrapper noBorder style={styles.topBorder}>
          <Meta color={theme.colors.gray300}>
            {t('account.benefitDetail.identifier')}
          </Meta>
          {identifiers.map((id) => (
            <BaseText key={id} style={styles.rowValue}>
              {id}
            </BaseText>
          ))}
        </ItemWrapper>
      )}

      {verifiedAt && (
        <RowSpaceBetween withTopBorder>
          <Meta color={theme.colors.gray300}>
            {t('account.benefitDetail.verified')}
          </Meta>
          <BaseText style={styles.rowValue}>{formatDate(verifiedAt)}</BaseText>
        </RowSpaceBetween>
      )}

      {validFrom && (
        <RowSpaceBetween withTopBorder>
          <Meta color={theme.colors.gray300}>
            {t('account.benefitDetail.valid')}
          </Meta>
          <BaseText style={styles.rowValue}>{formatDate(validFrom)}</BaseText>
        </RowSpaceBetween>
      )}

      {showDisconnect && (
        <StyledWrapper mt={24}>
          <Meta color={theme.colors.gray300}>
            {t('account.benefitDetail.disconnectInfo')}
          </Meta>
          <StyledWrapper mt={16}>
            <Button
              {...testIdentifiers('disconnect-benefit-button')}
              size="l"
              text={t('account.benefitDetail.disconnect')}
              onPress={() =>
                useUiStore.getState().setSheetModal('disconnectBenefit')
              }
              buttonType="tertiary"
            />
          </StyledWrapper>
        </StyledWrapper>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  benefitName: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.colors.lightBg.green,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowValue: {
    fontWeight: '500',
  },
  topBorder: {
    borderTopWidth: 3,
    borderTopColor: theme.colors.gray50,
  },
});

export default BenefitDetailModal;
